// ClauseReferenceTypeahead — combo-box for the clause-reference field
// in the add modal. Combines a free-text input with a dropdown of
// existing clause-references (deduped + sorted) drawn from the
// React Query cache. The user can pick a suggestion or type something
// brand-new; a value not in the suggestions list is accepted as-is
// so introducing a new clause reference isn't blocked.

import { useEffect, useRef, useState } from "react";

export interface ClauseReferenceTypeaheadProps {
  value: string;
  onChange: (next: string) => void;
  suggestions: string[];
  id: string;
  label: string;
}

export default function ClauseReferenceTypeahead({
  value,
  onChange,
  suggestions,
  id,
  label,
}: ClauseReferenceTypeaheadProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Local mirror of the input's text so the dropdown filter narrows
  // responsively even when the parent's controlled value lags (e.g. a
  // reducer-based form state that hasn't flushed yet). Re-syncs whenever
  // the controlled value changes from outside.
  const [inputText, setInputText] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    setInputText(value);
  }

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  // Filter suggestions case-insensitively against the current input
  // text; an empty text shows everything so the user can browse.
  const trimmedQuery = inputText.trim().toLowerCase();
  const filtered = trimmedQuery
    ? suggestions.filter((s) => s.toLowerCase().includes(trimmedQuery))
    : suggestions;

  const handleSelect = (option: string) => {
    setInputText(option);
    onChange(option);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <label
        htmlFor={id}
        className="block text-caption font-medium text-slate-700 dark:text-slate-300"
      >
        {label}
      </label>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={`${id}-suggestions`}
        value={value}
        onChange={(e) => {
          setInputText(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-body-sm text-slate-900 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 focus:border-brand-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
      />
      {open && filtered.length > 0 && (
        <ul
          id={`${id}-suggestions`}
          data-testid="clause-ref-suggestions"
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-slate-200 bg-white p-1 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800"
        >
          {filtered.map((option) => (
            <li key={option}>
              <button
                type="button"
                role="option"
                aria-selected={option === value}
                onClick={() => handleSelect(option)}
                className="block w-full rounded px-2 py-1 text-left text-body-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                {option}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
